function round10(number) {
    return Math.round(number * 100) / 100;
}

/**
 * Convert polar coordinates into cartesian coordinates
 *
 * Zero degrees is considered to be along the -x axis.
 *
 * Adapted from http://stackoverflow.com/a/18473154/1435658
 *
 * @param  {number} centerX        - x position of polar origin
 * @param  {number} centerY        - y position of polar origin
 * @param  {number} radius         - distance from polar origin to point
 * @param  {number} angleInDegrees - angle to point in degrees
 * @return {object}                - x, y points in cartesian space
 */
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 180) * Math.PI) / 180.0;

    return {
        x: round10(centerX + radius * Math.cos(angleInRadians)),
        y: round10(centerY + radius * Math.sin(angleInRadians)),
    };
}

/**
 * Creates an svg path `d` attribute that describes an arc.
 *
 * Adapted from http://stackoverflow.com/a/18473154/1435658
 *
 * @param  {object} origin       - center of the arc
 * @param  {number} origin.x     - x-position of center point
 * @param  {number} origin.y     - y-position of center point
 * @param  {number} radius       - Distance from origin to the arc
 * @param  {object} sweep        - Angles in degrees to start and top the arc
 * @param  {number} sweep.start  - Angle to start the arc
 * @param  {number} sweep.end    - Angle to end the arc
 * @return {string}              - `d` param for an SVG path
 */
export function describeArc(origin, radius, sweep) {
    const start = polarToCartesian(origin.x, origin.y, radius, sweep.end);
    const end = polarToCartesian(origin.x, origin.y, radius, sweep.start);

    const arcSweep = sweep.end - sweep.start <= 180 ? '0' : '1';

    return ['M', start.x, start.y, 'A', radius, radius, 0, arcSweep, 0, end.x, end.y].join(' ');
}

/**
 * Creates an svg path `d` attribute that describes a radial line segement.
 *
 * Adapted from http://stackoverflow.com/a/18473154/1435658
 *
 * @param  {object} origin       - center of the arc
 * @param  {number} origin.x     - x-position of center point
 * @param  {number} origin.y     - y-position of center point
 * @param  {object} radius       - Start and stop of the line
 * @param  {number} radius.inner - Distance from origin to the inner end of the line
 * @param  {number} radius.outer - Distance from origin to the outer end of the line
 * @param  {number} angle        - Angle in degrees the line lies along
 * @return {string}              - `d` param for an SVG path
 */
export function describeRadialLineSegment(origin, radius, angle) {
    const inner = polarToCartesian(origin.x, origin.y, radius.inner, angle);
    const outer = polarToCartesian(origin.x, origin.y, radius.outer, angle);

    return ['M', inner.x, inner.y, 'L', outer.x, outer.y].join(' ');
}
